import { createI18n } from 'vue-i18n';

import en from './locales/en.json';
import ru from './locales/ru.json';

export const LOCALES = ['ru', 'en'] as const;
export type AppLocale = (typeof LOCALES)[number];

const STORAGE_KEY = 'events.locale';

/**
 * Язык интерфейса.
 *
 * Русский по умолчанию, английский — с первого дня: на мероприятия приезжают
 * иностранные делегации, и дописывать i18n задним числом дороже, чем вести два
 * файла сразу.
 *
 * Выбор запоминается в браузере, а не в профиле: страницу заявки открывает
 * человек, которого в системе ещё нет, и профиля у него тоже нет.
 */
function initial(): AppLocale {
  try {
    const saved = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'en') return saved;
    // Язык браузера — разумная догадка для первого захода: человек по ссылке
    // из письма приходит со стороны, и спрашивать его о языке до того, как он
    // увидел страницу, не о чем.
    const browser = globalThis.navigator?.language ?? '';
    if (browser.toLowerCase().startsWith('en')) return 'en';
  } catch {
    /* хранилище недоступно — остаёмся на русском */
  }
  return 'ru';
}

/**
 * Русские склонения числительных.
 *
 * Без своего правила vue-i18n считает по английскому — «одна или много» — и
 * из трёх записанных форм берёт вторую при любом числе, кроме единицы:
 * «через 5 дня». Порядок форм в сообщении: 1 | 2–4 | 5–20. То же правило,
 * что в lk-web.
 */
function russianPlural(choice: number, choicesLength: number): number {
  if (choicesLength < 3) return choice === 1 ? 0 : 1;
  const n = Math.abs(choice);
  const tens = n % 100;
  const ones = n % 10;
  if (tens >= 11 && tens <= 14) return 2;
  if (ones === 1) return 0;
  if (ones >= 2 && ones <= 4) return 1;
  return 2;
}

export const i18n = createI18n({
  legacy: false,
  locale: initial(),
  fallbackLocale: 'ru',
  messages: { ru, en },
  pluralRules: { ru: russianPlural },
  // Без описанных форматов `d(date, 'short')` молча отдаёт пустую строку:
  // в карточке стояло «дал сам ·» без даты, в списке — пустая «Создана».
  datetimeFormats: {
    ru: { short: { day: '2-digit', month: '2-digit', year: 'numeric' } },
    en: { short: { day: 'numeric', month: 'short', year: 'numeric' } },
  },
});

/** Сменить язык и запомнить выбор. */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  document.documentElement.setAttribute('lang', locale);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    /* не сохранилось — язык всё равно переключён */
  }
}
